/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from 'react-bootstrap';
import { fetchAllReviews, deleteReview, generateRegistrationLink, generateEmailTemplate } from '../../_utilities/reviewUtils';
import { formatDate } from '../../_utilities/formattingUtils';
import { renderAlert } from '../../_utilities/formattingUtils';

const WebsiteReviewManager = () => {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    useEffect(() => {
        loadReviews();
    }, []);

    const loadReviews = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchAllReviews();
            setReviews(data);
        } catch (err) {
            setError('Failed to load reviews: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (reviewId, prospectName) => {
        if (!window.confirm(`Are you sure you want to delete the review for ${prospectName}? This cannot be undone.`)) {
            return;
        }

        try {
            await deleteReview(reviewId);
            setReviews(prev => prev.filter(r => r.review_id !== reviewId));
            setSuccessMsg('Review deleted successfully');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError('Failed to delete review: ' + err.message);
        }
    };

    const handleCopyLink = (reviewId) => {
        const link = generateRegistrationLink(reviewId);
        navigator.clipboard.writeText(link).then(() => {
            setSuccessMsg('Registration link copied to clipboard!');
            setTimeout(() => setSuccessMsg(''), 3000);
        }).catch(err => {
            setError('Failed to copy link: ' + err.message);
        });
    };

    const handleCopyEmail = (review) => {
        const emailText = generateEmailTemplate(review);
        navigator.clipboard.writeText(emailText).then(() => {
            setSuccessMsg('Email template copied to clipboard!');
            setTimeout(() => setSuccessMsg(''), 3000);
        }).catch(err => {
            setError('Failed to copy email: ' + err.message);
        });
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'secondary',
            sent: 'info',
            viewed: 'primary',
            converted: 'success'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="component-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Website Reviews</h3>
                <Button 
                    variant="primary" 
                    onClick={() => navigate('/admins/reviews/new')}
                >
                    <i className="bi bi-plus-circle me-2"></i>
                    Create New Review
                </Button>
            </div>

            {error && renderAlert(error, 'danger')}
            {successMsg && renderAlert(successMsg, 'success')}

            {reviews.length === 0 ? (
                <div className="alert alert-info">
                    No reviews found. Create your first review to get started!
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Prospect</th>
                                <th>Company</th>
                                <th>Status</th>
                                <th>Clicks</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.map(review => (
                                <tr key={review.review_id}>
                                    <td>{review.review_id}</td>
                                    <td>
                                        {review.prospect_name}
                                        <br/>
                                        <small className="text-muted">{review.prospect_email}</small>
                                    </td>
                                    <td>
                                        <a href={review.prospect_url} target="_blank" rel="noopener noreferrer">
                                            {review.prospect_company}
                                        </a>
                                    </td>
                                    <td>{getStatusBadge(review.status)}</td>
                                    <td>{review.click_count || 0}</td>
                                    <td>{formatDate(review.created_at, true)}</td>
                                    <td>
                                        <div className="btn-group btn-group-sm">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => navigate(`/admins/reviews/${review.review_id}`)}
                                                title="Edit"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </Button>
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                onClick={() => handleCopyLink(review.review_id)}
                                                title="Copy Registration Link"
                                            >
                                                <i className="bi bi-link-45deg"></i>
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => handleCopyEmail(review)}
                                                title="Copy Email Template"
                                            >
                                                <i className="bi bi-envelope"></i>
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(review.review_id, review.prospect_name)}
                                                title="Delete"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default WebsiteReviewManager;
